import { Controller } from "@hotwired/stimulus";
import * as mapboxgl from 'mapbox-gl';
import { Map, MapboxEvent, Marker, LngLat } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import {
    CircleMode,
    DragCircleMode,
    DirectMode,
    SimpleSelectMode
} from 'mapbox-gl-draw-circle';
import { DrawActionableEvent, DrawModeChageEvent, DrawSelectionChangeEvent } from '@mapbox/mapbox-gl-draw';
import * as StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import { Polygon } from 'geojson';

import { GpsCoordinates, ITriggerZone } from '../../scripts/sharedModels';
import { createMap, getDrawStyles, buildMapFeatureForTriggerZone, DEFAULT_CIRCLE_TRIGGER_ZONE_RADIUS_M } from '../../scripts/mapbox';
import { forceMapToRedrawWhenInitiallyHidden } from "./MapComponentShared";
import { IGpsCoordinates } from "../../scripts/_generated/IGpsCoordinates";
import { TriggerZoneType } from "../../scripts/_generated/TriggerZoneType";

/* TODOs:
- polygon ukoncit po 3. bodu
    - nevidim na to zadnou event, tak muzeme aspon ukazovat hezky hint viz https://github.com/mapbox/mapbox-gl-draw/issues/1003#issuecomment-683479729
 */

export const mapEditedEventName = "mapEdited";

export default class MapController extends Controller {
    static targets = ['container', 'deleteButton', 'markerLatitude', 'markerLongitude', 'triggerZones'];
    static values = {
    }

    readonly containerTarget!: HTMLElement;
    readonly deleteButtonTarget!: HTMLElement;
    readonly markerLatitudeTarget!: HTMLInputElement;
    readonly markerLongitudeTarget!: HTMLInputElement;
    readonly triggerZonesTarget!: HTMLInputElement;

    readonlyValue = false;

    map!: Map;
    draw!: MapboxDraw;
    marker!: Marker;

    readonly defaultMapCenter = new mapboxgl.LngLat(0, 0);

    connect() {
        this.deleteButtonTarget.hidden = true;
        this.readonlyValue = !this.markerLatitudeTarget.name;

        this.map = createMap(this.containerTarget);

        forceMapToRedrawWhenInitiallyHidden(this.containerTarget, this.map);

        const drawModes = this.readonlyValue ?
            {
                static: StaticMode,
            } :
            {
                ...MapboxDraw.modes,
                draw_circle: CircleMode,
                drag_circle: DragCircleMode,
                direct_select: DirectMode,
                simple_select: SimpleSelectMode
            };

        this.draw = new MapboxDraw({
            defaultMode: this.readonlyValue ? 'static' : 'simple_select',
            userProperties: true,
            displayControlsDefault: false,
            // @ts-ignore
            modes: drawModes,
            // styling docs: https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/EXAMPLES.md
            styles: getDrawStyles()
        });

        this.map.addControl(this.draw);

        this.map.on('load', (e: MapboxEvent) => {
            // Draw only works after the Mapbox GL JS map has loaded, so you must interact with Draw only after your map's load event
            this.initializeMapFeatures();
        });

        this.map.on('draw.selectionchange', (e: DrawSelectionChangeEvent) => {
            //console.log('select:', this.draw.getMode(), e.features);

            if (e.features.length === 1 && e.features[0].id) {
                this.draw.changeMode('direct_select', { featureId: `${e.features[0].id}` });
            }
            this.deleteButtonTarget.hidden = e.features.length === 0;
        });

        this.map.on('draw.actionable', (e: DrawActionableEvent) => {
            //console.log(`actionable`, e.actions);
        });

        this.map.on('draw.modechange', (e: DrawModeChageEvent) => {
            //console.log(`modechange`, e.mode);
        });

        this.map.on('draw.create', (e: MapboxDraw.DrawCreateEvent) => {
            //console.log(`draw.create`, e.features);
            this.save();
        });

        this.map.on('draw.update', (e: MapboxDraw.DrawUpdateEvent) => {
            //console.log(`draw.update`, e.features);
            this.save();
        });

        this.map.on('draw.delete', (e: MapboxDraw.DrawDeleteEvent) => {
            //console.log(`draw.delete`, e.features);
            this.save();
        });

        this.map.on('draw.combine', (e: MapboxDraw.DrawCombineEvent) => {
            //console.log(`draw.combine`, e.createdFeatures, e.deletedFeatures);
            this.save();
        });

        this.map.on('draw.uncombine', (e: MapboxDraw.DrawUncombineEvent) => {
            //console.log(`draw.uncombine`, e.createdFeatures, e.deletedFeatures);
            this.save();
        });
    }

    initializeMapFeatures() {
        // => draw marker and all existing trigger zones or polyline
        const markerCoordinates = this.markerLatitudeTarget.value && this.markerLongitudeTarget.value ?
            new LngLat(JSON.parse(this.markerLongitudeTarget.value) as number, JSON.parse(this.markerLatitudeTarget.value) as number) :
            this.defaultMapCenter;
        this.marker = new Marker({
            color: "#31bb11",
            draggable: !this.readonlyValue,
            anchor: 'bottom',
        });
        this.marker.setLngLat(markerCoordinates);
        this.marker.addTo(this.map);
        this.marker.on('dragend', () => this.save());

        const triggerZones = JSON.parse(this.triggerZonesTarget.value) as ITriggerZone[];
        triggerZones.map(tz => buildMapFeatureForTriggerZone(tz, false)).forEach(this.draw.add);

        this.map.fitBounds(markerCoordinates.toBounds(250), { animate: false });
    }

    addCircle() {
        // @ts-ignore
        this.draw.changeMode('draw_circle', { initialRadiusInKm: DEFAULT_CIRCLE_TRIGGER_ZONE_RADIUS_M / 1000 });
    }

    addPolygon() {
        this.draw.changeMode('draw_polygon');
    }

    delete() {
        const selectedIds = this.draw.getSelectedIds();
        this.draw.delete(selectedIds);
        this.deleteButtonTarget.hidden = true;
        this.save();
    }

    save() {
        if (this.readonlyValue) return;

        const markerCoordinates = this.marker.getLngLat();
        this.markerLatitudeTarget.value = JSON.stringify(markerCoordinates.lat);
        this.markerLongitudeTarget.value = JSON.stringify(markerCoordinates.lng);

        const allFeatures = this.draw.getAll();
        const triggerZones =
            allFeatures.features
                .filter(f => f.type === 'Feature' && f.geometry.type === 'Polygon')
                .map((f): ITriggerZone => {
                    if (f.properties?.isCircle) {
                        return {
                            type: TriggerZoneType.Circle,
                            circleTypeRadius: f.properties?.radiusInKm * 1000,
                            circleTypeGpsCoordinates: new GpsCoordinates(
                                f.properties?.center[1],
                                f.properties?.center[0]
                            )
                        };
                    }
                    return {
                        type: TriggerZoneType.Polygon,
                        polygonTypePolygonCorners: (f.geometry as Polygon).coordinates[0].map(c => (new GpsCoordinates(c[1],c[0])))
                    };
                });
        this.triggerZonesTarget.value = JSON.stringify(triggerZones ?? []);

        this.element.dispatchEvent(new Event(mapEditedEventName, { bubbles: true }));
    }

    zoomIn() {
        this.map.zoomIn();
    }

    zoomOut() {
        this.map.zoomOut();
    }

    setMarkerCoordinates(coordinates: IGpsCoordinates) {
        this.marker.setLngLat([coordinates.longitude, coordinates.latitude]);
    }

    setTriggerZones(triggerZones: ITriggerZone[]) {
        this.draw.deleteAll();
        triggerZones.map(tz => buildMapFeatureForTriggerZone(tz, false)).forEach(this.draw.add);
    }

    latitudeChanged() {
        if (!$(this.markerLatitudeTarget).valid()) return;

        const markerCoordinates = this.marker.getLngLat();
        const newMarkerLatitude = Number.parseFloat(this.markerLatitudeTarget.value);
        if (!isNaN(newMarkerLatitude)) {
            this.marker.setLngLat([markerCoordinates.lng, newMarkerLatitude]);
        }
    }

    longitudeChanged() {
        if (!$(this.markerLongitudeTarget).valid()) return;

        const markerCoordinates = this.marker.getLngLat();
        const newMarkerLongitude = Number.parseFloat(this.markerLongitudeTarget.value);
        if (!isNaN(newMarkerLongitude)) {
            this.marker.setLngLat([newMarkerLongitude, markerCoordinates.lat]);
        }
    }
}
